import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { processDesignImages, getAllGlobalMergedImages } from '../utils/imageUtils';
import { exportImages } from '../utils/render3d'; 
import { mergeDesignToImage } from '../utils/imageUtils';
import { subscribeAITaskCompleted } from '../utils/aiEvents';
 
interface MergedImageData {
  image: string;
  code: string;
  timestamp: number;
}
interface PreviewWindowProps {
  isVisible?: boolean;
  onClose?: () => void;
  selectedTemplate?: any;
  loadedProduct?: any;
  code?: string;
}

const PreviewWindow: React.FC<PreviewWindowProps> = ({ 
  isVisible = true, 
  onClose,
  selectedTemplate,
  loadedProduct,
  code
}) => {
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [processedImageData, setProcessedImageData] = useState<{[key: number]: string[]}>({});
  const [processTimeout, setProcessTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showGlobalData, setShowGlobalData] = useState(false);
  const [aiRefreshTick, setAiRefreshTick] = useState(0);


  const handleTemplateImageProcess = async (template: any, index: number) => {
    try {
      const processedImages = await processDesignImages('', template);
      if (processedImages && processedImages.length > 0) {
        const firstGroup = processedImages.find((group: any) => group && group.image);
        if (firstGroup && firstGroup.image && Array.isArray(firstGroup.image) && firstGroup.image.length > 0) {
          const validImages = firstGroup.image.filter((img: any) => img && img.trim() !== '');
          if (validImages.length > 0) {
            setProcessedImageData(prev => ({
              ...prev,
              [index]: validImages
            }));
          }
        }
      }
    } catch (error) {
      console.error('处理模板图片失败:', error);
    }
  };

  const processAllTemplateImages = async (selectedTemplate: any=null) => { 
    // 清除之前的定时器
    if (processTimeout) {
      clearTimeout(processTimeout);
    }  
    // 设置新的定时器，1秒后执行
    const newTimeout = setTimeout(async () => {
      if (loadedProduct && loadedProduct.templates) {
        let template = null; 
        if (selectedTemplate) template = loadedProduct.templates[selectedTemplate-1];
        else template = loadedProduct.templates[0]; 

        if (!template) {
          console.error("selectedTemplate not found");
          return false;
        }
        
        if (template.design) {
          await mergeDesignToImage('', template.design, 800);
        }
         
        for (let i = 0; i < loadedProduct.templates.length; i++) { 
          const template = loadedProduct.templates[i];
          
          if (!template.design) { 
            continue;
          }
           
          // 将数组转换为对象格式，以 code 为键
          const mergedImagesArray = getAllGlobalMergedImages();
          console.log(mergedImagesArray)
          const screenshotObject = [] as MergedImageData[];
          mergedImagesArray.forEach(item => {
            if (!screenshotObject.find((it) => it.code === item.code)) {
              screenshotObject.push(item);
            }
          });  
          const exportResult = await exportImages(
            {"screenshot": screenshotObject, "urlPath": "http://localhost:3000/"}, 
            template.design
          ) 
          console.log(exportResult)
          // exportImages 返回的是 tempImageAll 对象，需要提取图片数据
          if (exportResult.length) {
            // 遍历所有 option_value_id 获取图片数据
            const allImages: string[] = [];
            for (const key in exportResult) {
              if (exportResult[key] && exportResult[key].image && Array.isArray(exportResult[key].image)) {
                allImages.push(...exportResult[key].image);
              }
            } 
            if (allImages.length > 0) { 
              setProcessedImageData(prev => {
                const newData = {
                  ...prev,
                  [i]: allImages
                }; 
                return newData;
              });
            } 
          } else {
            console.log(`模板 ${i} 没有有效的导出结果`);
          }
        } 
      } else {
        console.log("没有找到 loadedProduct 或 templates");
      }
      setProcessTimeout(null);
    }, 1000);

    setProcessTimeout(newTimeout);
  };
 
  useEffect(() => {
    const off = subscribeAITaskCompleted(() => {
      setAiRefreshTick((t) => t + 1);
    });
    return () => off();
  }, []);
  // 组件首次加载时执行
  useEffect(() => {
    if (loadedProduct && loadedProduct.templates) {
      console.log("processAllTemplateImages..2")
      processAllTemplateImages();
    }
  }, [loadedProduct]); // 空依赖数组，只在组件挂载时执行一次

  // 清理定时器
  useEffect(() => {
    return () => {
      if (processTimeout) {
        clearTimeout(processTimeout);
      }
    };
  }, [processTimeout]);

  // 生成产品图片数据
  const productImages = loadedProduct ? [
    ...loadedProduct.templates.map((template: any, index: number) => ({
      id: `template-${index}`,
      preview: template.preview,
      alt: template.name,
      isTemplate: template.design ? true : false,
      template: template
    }))
  ] : [];
 

  const nextPreview = () => {
    setCurrentPreviewIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevPreview = () => {
    setCurrentPreviewIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (isFullScreen) {
      setIsFullScreen(false);
    }
  };

  if (!isVisible) return null;

  // If minimized, show as navigation bar at bottom
  if (isMinimized) {
    return (
      <div className="fixed bottom-0 right-4 z-[500]">
        <div className="bg-gray-100 rounded-t-lg shadow-xl w-80">
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleMinimize}
                className="p-1 hover:bg-gray-100 rounded"
                title="Expand"
              >
                <Bars3Icon className="w-5 h-5 text-gray-600" />
              </button>
              <h3 className="font-semibold text-gray-800 text-sm">Preview</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="p-1 hover:bg-gray-100 rounded"
                title="Toggle Full Screen"
              >
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed z-[500] ${isFullScreen ? 'inset-0 bg-black' : 'bottom-4 right-4'}`}>
      <div className={`bg-white rounded-lg shadow-xl ${isFullScreen ? 'w-full h-full' : 'w-80 max-h-96'}`}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
            <button
              onClick={toggleMinimize}
              className="p-1 hover:bg-gray-100 rounded"
              title="Minimize"
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            </button>
                  <h3 className="font-semibold text-gray-800">Preview</h3>
                </div>
                <div className="flex items-center space-x-2"> 
                  <button
                    onClick={() => setIsFullScreen(!isFullScreen)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Toggle Full Screen"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="relative">
                {/* Global Data Display */}
                {/* {showGlobalData && (
                  <div className="p-4 bg-gray-50 border-b border-gray-200 max-h-32 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Global Merged Images:</h4>
                    <div className="text-xs text-gray-600">
                      {(() => {
                        const globalData = getAllGlobalMergedImages();
                        const keys = Object.keys(globalData);
                        if (keys.length === 0) {
                          return <span className="text-gray-400">No data available</span>;
                        }
                        return keys.map((code) => (
                          <div key={code} className="mb-1">
                            <span className="font-medium">Code: {code}</span> - {globalData[code].length} images
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )} */}
                
                {/* Carousel */}
                <div className="relative overflow-hidden">
                  <div className="flex transition-transform duration-300 ease-in-out" style={{ transform: `translateX(-${currentPreviewIndex * 100}%)` }}>
                    {productImages.map((image, index) => (
                      <div key={image.id} className="w-full flex-shrink-0">
                        <div className="py-4">
                          <div
                            className={
                              isFullScreen
                                ? "inset-0 z-[2000] bg-black/80 flex items-center justify-center"
                                : "w-48 h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mx-auto relative overflow-hidden"
                            }
                            onClick={() => setIsFullScreen((prev) => !prev)}
                          >
                            {image.isTemplate ? (
                              processedImageData[index] && processedImageData[index].length > 0 ? (
                                <div className={isFullScreen ? "relative w-[80vw] h-[80vh]" : "w-full h-full relative"}>
                                  {processedImageData[index].map((imgSrc, imgIndex) => (
                                    <img
                                      key={imgIndex}
                                      src={imgSrc}
                                      alt={`Processed Template Layer ${imgIndex + 1}`}
                                      className={
                                        isFullScreen
                                          ? "absolute inset-0 w-full h-full object-contain"
                                          : "absolute inset-0 w-full h-full object-cover"
                                      }
                                      style={{ zIndex: imgIndex }}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <svg
                                  className="h-10 w-10 text-gray-400 animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  aria-label="Processing"
                                  role="img"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M12 4v4a4 4 0 014 4h4c0-4.418-3.582-8-8-8z"
                                  ></path>
                                </svg>
                              )
                            ) : (
                              <img
                                src={image.preview}
                                alt={image.alt}
                                className={isFullScreen ? "max-w-[80vw] max-h-[80vh] object-contain" : "w-full h-full object-cover"}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevPreview}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextPreview}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                {/* Pagination Dots */}
                <div className="flex justify-center space-x-2 p-4">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPreviewIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentPreviewIndex ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
  );
};

export default PreviewWindow;